import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { competitionsAPI, leaguesAPI } from '@/lib/api';
import { getStatusLabel } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Trophy } from 'lucide-react';
import { ImageCropUpload } from '@/components/ImageCropUpload';

export const CreateCompetitionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [myLeagues, setMyLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);


  const [formData, setFormData] = useState({
    league_id: searchParams.get('league') || '',
    sport_id: '',
    name: '',
    status: 'draft',
    competition_type: 'league',
    registration_mode: 'teams',
    format: 'round_robin',
    max_teams: '',
    start_date: '',
    end_date: '',
    season_label: '',
    description: '',
    cover_image_url: ''
  });

  useEffect(() => {
    if (isEditing) return;
    const fetchLeagues = async () => {
      try {
        const res = await leaguesAPI.getMyLeagues();
        setMyLeagues(res.data || []);
        if (formData.league_id) {
          const league = res.data.find(l => l.league_id === formData.league_id);
          setSelectedLeague(league || null);
          if (league) {
            setFormData(prev => ({ ...prev, sport_id: league.sport_id }));
          }
        }
      } catch (error) {
        console.error('Error fetching leagues:', error);
      }
    };
    fetchLeagues();
  }, [formData.league_id, isEditing]);

  useEffect(() => {
    const fetchCompetition = async () => {
      if (!isEditing) return;
      setFetching(true);
      setLoading(true);
      try {
        const res = await competitionsAPI.getById(id);
        const comp = res.data.competition;
        setFormData({
          league_id: comp.league_id || '',
          sport_id: comp.sport_id || '',
          name: comp.name || '',
          status: comp.status || 'draft',
          competition_type: comp.competition_type || 'league',
          registration_mode: comp.registration_mode || 'teams',
          format: comp.format || 'round_robin',
          max_teams: comp.max_teams ? String(comp.max_teams) : '',
          start_date: comp.start_date ? new Date(comp.start_date).toISOString().slice(0, 10) : '',
          end_date: comp.end_date ? new Date(comp.end_date).toISOString().slice(0, 10) : '',
          season_label: comp.season_label || '',
          description: comp.description || '',
          cover_image_url: comp.cover_image_url || ''
        });
        if (res.data.league) {
          setSelectedLeague(res.data.league);
        }
      } catch (error) {
        toast.error('Errore nel caricamento competizione');
      } finally {
        setFetching(false);
        setLoading(false);
      }
    };
    fetchCompetition();
  }, [id, isEditing]);

  const handleLeagueChange = (leagueId) => {
    const league = myLeagues.find(l => l.league_id === leagueId);
    setSelectedLeague(league || null);
    setFormData(prev => ({
      ...prev,
      league_id: leagueId,
      sport_id: league?.sport_id || ''
    }));
  };

  const handleTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      competition_type: value,
      format: value === 'tournament' ? 'auto' : 'round_robin'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.league_id || !formData.name) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        max_teams: formData.max_teams ? parseInt(formData.max_teams, 10) : undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        season_label: formData.season_label || undefined,
        description: formData.description || undefined,
        cover_image_url: formData.cover_image_url || undefined
      };

      if (isEditing) {
        await competitionsAPI.update(id, payload);
        toast.success('Competizione aggiornata!');
        navigate(`/competitions/${id}`);
      } else {
        const res = await competitionsAPI.create(payload);
        toast.success('Competizione creata!');
        navigate(`/competitions/${res.data.competition_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };
  if (fetching) return <p role="status" className="p-6">Caricamento competizione…</p>;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto" data-testid="create-competition-page">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            {isEditing ? 'Modifica Competizione' : 'Crea Competizione'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="competition-league">Lega *</Label>
              {isEditing ? (
                <Input
                  id="competition-league"
                  value={selectedLeague?.name || formData.league_id}
                  readOnly
                />
              ) : (
                <Select value={formData.league_id} onValueChange={handleLeagueChange}>
                  <SelectTrigger id="competition-league" data-testid="competition-league-select">
                    <SelectValue placeholder="Seleziona lega" />
                  </SelectTrigger>
                  <SelectContent>
                    {myLeagues.map(league => (
                      <SelectItem key={league.league_id} value={league.league_id}>
                        {league.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedLeague && (
                <p className="text-xs text-muted-foreground">
                  Sport: {selectedLeague.sport_name || selectedLeague.sport_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Nome competizione *</Label>
              <Input
                placeholder="Es: Stagione Invernale 2025"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Copertina</Label>
              <ImageCropUpload
                value={formData.cover_image_url}
                onChange={(url) => setFormData(prev => ({ ...prev, cover_image_url: url }))}
                preset="cover"
              />
            </div>

            {isEditing && (
              <div className="space-y-2">
                <Label htmlFor="competition-status">Stato</Label>
                <select
                  id="competition-status"
                  value={formData.status}
                  onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full rounded-md border bg-background p-3 text-base"
                  disabled={loading}
                >
                  {['draft', 'registration', 'in_progress', 'completed', 'cancelled'].map(status => (
                    <option key={status} value={status}>{getStatusLabel(status)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={formData.competition_type} onValueChange={handleTypeChange} disabled={isEditing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="league">Campionato</SelectItem>
                    <SelectItem value="tournament">Torneo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Iscrizione *</Label>
                <Select
                  value={formData.registration_mode}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, registration_mode: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teams">Squadre</SelectItem>
                    <SelectItem value="players">Giocatori</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Formato</Label>
                <Select
                  value={formData.format}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, format: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.competition_type === 'tournament' ? (
                      <>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="elimination">Eliminazione</SelectItem>
                        <SelectItem value="groups">Gironi</SelectItem>
                        <SelectItem value="groups_elimination">Gironi + Eliminazione</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="round_robin">Round Robin</SelectItem>
                        <SelectItem value="groups">Gironi</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.registration_mode === 'teams' && (
                <div className="space-y-2">
                  <Label>Max squadre</Label>
                  <Input
                    type="number"
                    min="2"
                    max="200"
                    value={formData.max_teams}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_teams: e.target.value }))}
                    placeholder="es. 16"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inizio</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Fine</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Stagione (opzionale)</Label>
              <Input
                placeholder="Es: 2025/26"
                value={formData.season_label}
                onChange={(e) => setFormData(prev => ({ ...prev, season_label: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Regole, premi, info utili..."
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? (isEditing ? 'Salvataggio...' : 'Creazione...') : (isEditing ? 'Salva Modifiche' : 'Crea Competizione')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateCompetitionPage;
